/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Platform Settings Page - SuperAdmin Portal
 * ============================================================================
 *
 * Configure platform-wide settings including regional, features, trial,
 * platform configuration, and security options.
 *
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings, Save, Globe, Zap, Gift, Building2, Shield,
  CheckCircle, XCircle, AlertTriangle, Eye, EyeOff, RefreshCw
} from 'lucide-react';
import { settingsService } from '@/services/superadmin/settings.service';
import { LayoutStylePicker } from '@/components/settings/LayoutStylePicker';
import { BottomNavStylePicker } from '@/components/settings/BottomNavStylePicker';
import { MobileModuleSwitchPicker } from '@/components/settings/MobileModuleSwitchPicker';

// Types
interface PlatformSetting {
  id: string;
  settingKey: string;
  settingValue: string;
  valueType: 'STRING' | 'INTEGER' | 'BOOLEAN' | 'JSON';
  category: string;
  description: string;
  isSensitive: boolean;
  isEditable: boolean;
}

interface Category {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
}

// Category Icon Component
const CategoryIcon: React.FC<{ category: string; className?: string }> = ({ category, className = "w-5 h-5" }) => {
  const icons: Record<string, React.ReactNode> = {
    REGIONAL: <Globe className={className} />,
    FEATURES: <Zap className={className} />,
    TRIAL: <Gift className={className} />,
    PLATFORM: <Building2 className={className} />,
    SECURITY: <Shield className={className} />,
  };
  return <>{icons[category] || <Settings className={className} />}</>;
};

// Toggle Switch Component
const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}> = ({ checked, onChange, disabled }) => (
  <button
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`toggle-switch ${checked ? 'toggle-on' : 'toggle-off'} ${disabled ? 'toggle-disabled' : ''}`}
  >
    <span className={`toggle-thumb ${checked ? 'toggle-thumb-on' : 'toggle-thumb-off'}`} />
  </button>
);

// Main Settings Page
export default function SettingsPage() {
  const { t } = useTranslation('platform');
  const [settings, setSettings] = useState<PlatformSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState('REGIONAL');
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [showSensitive, setShowSensitive] = useState<Record<string, boolean>>({});

  const categories: Category[] = [
    { id: 'REGIONAL', name: t('settings.categories.regional.name'), icon: <Globe className="w-5 h-5" />, description: t('settings.categories.regional.description') },
    { id: 'FEATURES', name: t('settings.categories.features.name'), icon: <Zap className="w-5 h-5" />, description: t('settings.categories.features.description') },
    { id: 'TRIAL', name: t('settings.categories.trial.name'), icon: <Gift className="w-5 h-5" />, description: t('settings.categories.trial.description') },
    { id: 'PLATFORM', name: t('settings.categories.platform.name'), icon: <Building2 className="w-5 h-5" />, description: t('settings.categories.platform.description') },
    { id: 'SECURITY', name: t('settings.categories.security.name'), icon: <Shield className="w-5 h-5" />, description: t('settings.categories.security.description') },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await settingsService.getSettings();
      setSettings(data as PlatformSetting[]);
      const initial: Record<string, string> = {};
      (data as PlatformSetting[]).forEach((s: PlatformSetting) => {
        initial[s.settingKey] = s.settingValue || '';
      });
      setEditedValues(initial);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      showNotification('error', t('settings.notifications.fetchError', 'Failed to load settings'));
      setSettings([]);
      setEditedValues({});
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      // Find changed values
      const changes: Record<string, string> = {};
      settings.forEach(s => {
        if (s.isEditable && editedValues[s.settingKey] !== s.settingValue) {
          changes[s.settingKey] = editedValues[s.settingKey];
        }
      });

      if (Object.keys(changes).length === 0) {
        showNotification('info', t('settings.notifications.noChanges'));
        setSaving(false);
        return;
      }

      // Use the authenticated settings service - pass changes as key-value map
      await settingsService.batchUpdateSettings(changes);

      showNotification('success', t('settings.notifications.savedCount', { count: Object.keys(changes).length }));
      // Refresh settings from server to confirm persistence
      fetchSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      showNotification('error', t('settings.notifications.saveError', 'Failed to save settings. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const handleValueChange = (key: string, value: string) => {
    setEditedValues(prev => ({ ...prev, [key]: value }));
  };

  const toggleSensitiveVisibility = (key: string) => {
    setShowSensitive(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const hasChanges = () => {
    return settings.some(s => s.isEditable && editedValues[s.settingKey] !== s.settingValue);
  };

  const filteredSettings = settings.filter(s => s.category === activeCategory);
  const activeCategory_ = categories.find(c => c.id === activeCategory);

  const formatSettingName = (key: string) => {
    return key.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const renderSettingInput = (setting: PlatformSetting) => {
    const value = editedValues[setting.settingKey] || '';
    const isChanged = value !== setting.settingValue;
    const isVisible = showSensitive[setting.settingKey];

    if (setting.valueType === 'BOOLEAN') {
      return (
        <div className="setting-input-row">
          <ToggleSwitch
            checked={value === 'true'}
            onChange={(checked) => handleValueChange(setting.settingKey, checked ? 'true' : 'false')}
            disabled={!setting.isEditable}
          />
          <span className={`toggle-label ${value === 'true' ? 'toggle-label-on' : 'toggle-label-off'}`}>
            {value === 'true' ? t('settings.toggle.enabled') : t('settings.toggle.disabled')}
          </span>
          {isChanged && <span className="change-indicator" />}
        </div>
      );
    }

    if (setting.valueType === 'INTEGER') {
      return (
        <div className="setting-input-row">
          <input
            type="number"
            value={value}
            onChange={(e) => handleValueChange(setting.settingKey, e.target.value)}
            disabled={!setting.isEditable}
            className={`setting-input setting-input-number ${isChanged ? 'setting-input-changed' : ''} ${!setting.isEditable ? 'setting-input-readonly' : ''}`}
          />
          {isChanged && <span className="change-badge">{t('settings.badges.modified')}</span>}
        </div>
      );
    }

    // STRING type
    return (
      <div className="setting-input-row">
        <div className="setting-input-wrapper">
          <input
            type={setting.isSensitive && !isVisible ? 'password' : 'text'}
            value={value}
            onChange={(e) => handleValueChange(setting.settingKey, e.target.value)}
            disabled={!setting.isEditable}
            className={`setting-input setting-input-text ${isChanged ? 'setting-input-changed' : ''} ${!setting.isEditable ? 'setting-input-readonly' : ''}`}
          />
          {setting.isSensitive && (
            <button
              onClick={() => toggleSensitiveVisibility(setting.settingKey)}
              className="sensitive-toggle"
            >
              {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}
        </div>
        {isChanged && <span className="change-badge">{t('settings.badges.modified')}</span>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="settings-loading">
        <div className="spinner" />
        <span>{t('settings.loading')}</span>
      </div>
    );
  }

  return (
    <div className="settings-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <Settings className="w-6 h-6" /> {t('settings.title')}
          </h1>
          <p className="page-subtitle">{t('settings.subtitle')}</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges()}
          className={`btn btn-primary ${!hasChanges() ? 'btn-disabled' : ''}`}
        >
          {saving ? (
            <>
              <span className="btn-spinner" />
              {t('settings.buttons.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {t('settings.buttons.saveChanges')}
            </>
          )}
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
          {notification.type === 'error' && <XCircle className="w-5 h-5" />}
          {notification.type === 'info' && <AlertTriangle className="w-5 h-5" />}
          <span>{notification.message}</span>
        </div>
      )}

      {/* Layout Style Picker — client-side preference, no backend */}
      <div className="settings-section-card" style={{ marginBottom: '24px', padding: '20px', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-subtle)', borderRadius: '12px' }}>
        <LayoutStylePicker />
      </div>

      {/* Bottom Nav Style Picker — mobile sub-item display preference */}
      <div className="settings-section-card" style={{ marginBottom: '24px', padding: '20px', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-subtle)', borderRadius: '12px' }}>
        <BottomNavStylePicker />
      </div>

      {/* Mobile Module Switch Style Picker — only shown for multi-module users */}
      <div className="settings-section-card" style={{ marginBottom: '24px', padding: '20px', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-subtle)', borderRadius: '12px' }}>
        <MobileModuleSwitchPicker />
      </div>

      {/* Main Content */}
      <div className="settings-layout">
        {/* Category Navigation */}
        <div className="category-nav">
          <nav className="category-list">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`category-item ${activeCategory === cat.id ? 'category-item-active' : ''}`}
              >
                <div className="category-icon">{cat.icon}</div>
                <div className="category-info">
                  <span className="category-name">{cat.name}</span>
                  <span className="category-desc">{cat.description}</span>
                </div>
              </button>
            ))}
          </nav>

          {/* Quick Stats */}
          <div className="quick-stats">
            <div className="quick-stat">
              <span className="quick-stat-value">{settings.length}</span>
              <span className="quick-stat-label">{t('settings.stats.totalSettings')}</span>
            </div>
            <div className="quick-stat">
              <span className="quick-stat-value">
                {settings.filter(s => s.valueType === 'BOOLEAN' && editedValues[s.settingKey] === 'true').length}
              </span>
              <span className="quick-stat-label">{t('settings.stats.featuresEnabled')}</span>
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        <div className="settings-panel">
          <div className="panel-header">
            <div className="panel-header-icon">
              <CategoryIcon category={activeCategory} />
            </div>
            <div>
              <h2 className="panel-title">{t('settings.panel.title', { category: activeCategory_?.name })}</h2>
              <p className="panel-desc">{activeCategory_?.description}</p>
            </div>
            <button onClick={fetchSettings} className="refresh-btn" title={t('settings.buttons.refresh')}>
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          <div className="settings-list">
            {filteredSettings.length === 0 ? (
              <div className="empty-state">
                <CategoryIcon category={activeCategory} className="w-12 h-12" />
                <p>{t('settings.empty')}</p>
              </div>
            ) : (
              filteredSettings.map(setting => (
                <div key={setting.id} className="setting-item">
                  <div className="setting-header">
                    <div className="setting-info">
                      <h3 className="setting-name">{formatSettingName(setting.settingKey)}</h3>
                      <p className="setting-desc">{setting.description}</p>
                    </div>
                    {!setting.isEditable && (
                      <span className="readonly-badge">{t('settings.badges.readOnly')}</span>
                    )}
                    {setting.isSensitive && (
                      <span className="sensitive-badge">{t('settings.badges.sensitive')}</span>
                    )}
                  </div>
                  <div className="setting-control">
                    {renderSettingInput(setting)}
                  </div>
                  <div className="setting-key">
                    {t('settings.key')}: <code>{setting.settingKey}</code>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Feature Flags Overview */}
      <div className="feature-flags-section">
        <h2 className="section-title">
          <Zap className="w-5 h-5" /> {t('settings.featureFlags.title')}
        </h2>
        <div className="feature-flags-grid">
          {settings.filter(s => s.valueType === 'BOOLEAN').map(setting => {
            const isEnabled = editedValues[setting.settingKey] === 'true';
            const isChanged = editedValues[setting.settingKey] !== setting.settingValue;
            return (
              <div key={setting.id} className={`feature-flag-card ${isEnabled ? 'feature-flag-on' : 'feature-flag-off'}`}>
                <span className="feature-flag-name">{formatSettingName(setting.settingKey)}</span>
                <div className="feature-flag-status">
                  {isChanged && <span className="change-dot" />}
                  <span className={`feature-flag-badge ${isEnabled ? 'badge-on' : 'badge-off'}`}>
                    {isEnabled ? t('settings.featureFlags.on') : t('settings.featureFlags.off')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{settingsStyles}</style>
    </div>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const settingsStyles = `
  .settings-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  /* Page Header */
  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .page-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-heading);
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text-primary);
  }

  .page-subtitle {
    font-size: 13px;
    color: var(--color-text-secondary);
    margin-top: 4px;
  }

  /* Loading State */
  .settings-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 64px;
    color: var(--color-text-muted);
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-border-subtle);
    border-top-color: var(--color-brand-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Notification */
  .notification {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
  }

  .notification-success {
    background: rgba(16, 185, 129, 0.15);
    color: #10B981;
    border: 1px solid rgba(16, 185, 129, 0.3);
  }

  .notification-error {
    background: rgba(239, 68, 68, 0.15);
    color: #EF4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
  }

  .notification-info {
    background: rgba(59, 130, 246, 0.15);
    color: #3B82F6;
    border: 1px solid rgba(59, 130, 246, 0.3);
  }

  /* Button */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 20px;
    height: 44px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-primary {
    background: var(--color-brand-primary);
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: var(--color-brand-primary-hover);
  }

  .btn-disabled, .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  /* Main Layout */
  .settings-layout {
    display: flex;
    gap: 24px;
  }

  /* Category Navigation */
  .category-nav {
    width: 280px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .category-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .category-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    text-align: left;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .category-item:hover {
    background: var(--color-bg-hover);
  }

  .category-item-active {
    background: var(--color-bg-secondary);
    border-color: var(--color-border-subtle);
  }

  .category-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-primary);
    border-radius: 8px;
    color: var(--color-text-secondary);
  }

  .category-item-active .category-icon {
    background: var(--color-brand-primary);
    color: white;
  }

  .category-info {
    flex: 1;
    min-width: 0;
  }

  .category-name {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .category-desc {
    display: block;
    font-size: 11px;
    color: var(--color-text-muted);
    margin-top: 2px;
  }

  /* Quick Stats */
  .quick-stats {
    display: flex;
    gap: 12px;
    padding: 16px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
  }

  .quick-stat {
    flex: 1;
    text-align: center;
  }

  .quick-stat-value {
    display: block;
    font-size: 24px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--color-text-primary);
  }

  .quick-stat-label {
    display: block;
    font-size: 11px;
    color: var(--color-text-muted);
    margin-top: 4px;
  }

  /* Settings Panel */
  .settings-panel {
    flex: 1;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    overflow: hidden;
  }

  .panel-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .panel-header-icon {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-brand-primary);
    border-radius: 10px;
    color: white;
  }

  .panel-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .panel-desc {
    font-size: 12px;
    color: var(--color-text-muted);
    margin-top: 2px;
  }

  .refresh-btn {
    margin-left: auto;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .refresh-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  /* Settings List */
  .settings-list {
    padding: 8px;
  }

  .setting-item {
    padding: 20px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .setting-item:last-child {
    border-bottom: none;
  }

  .setting-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
  }

  .setting-info {
    flex: 1;
  }

  .setting-name {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .setting-desc {
    font-size: 12px;
    color: var(--color-text-muted);
    margin-top: 4px;
    line-height: 1.4;
  }

  .readonly-badge {
    padding: 4px 8px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    color: var(--color-text-muted);
    text-transform: uppercase;
  }

  .sensitive-badge {
    padding: 4px 8px;
    background: rgba(245, 158, 11, 0.15);
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    color: #F59E0B;
    text-transform: uppercase;
  }

  .setting-control {
    margin-bottom: 8px;
  }

  .setting-key {
    font-size: 11px;
    color: var(--color-text-muted);
  }

  .setting-key code {
    padding: 2px 6px;
    background: var(--color-bg-primary);
    border-radius: 4px;
    font-family: var(--font-mono);
    font-size: 10px;
  }

  /* Setting Inputs */
  .setting-input-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: nowrap;
    min-width: 0;
  }

  .setting-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .setting-input {
    padding: 10px 14px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    font-size: 14px;
    color: var(--color-text-primary);
    outline: none;
    transition: border-color 0.15s ease;
  }

  .setting-input:focus {
    border-color: var(--color-brand-primary);
  }

  .setting-input-text {
    width: 300px;
  }

  .setting-input-number {
    width: 120px;
  }

  .setting-input-changed {
    border-color: #F59E0B;
  }

  .setting-input-readonly {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .sensitive-toggle {
    position: absolute;
    right: 10px;
    padding: 4px;
    background: none;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: color 0.15s ease;
  }

  .sensitive-toggle:hover {
    color: var(--color-text-primary);
  }

  .change-badge {
    padding: 4px 8px;
    background: rgba(245, 158, 11, 0.15);
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    color: #F59E0B;
  }

  .change-indicator {
    width: 8px;
    height: 8px;
    background: #F59E0B;
    border-radius: 50%;
  }

  /* Toggle Switch */
  .toggle-switch {
    position: relative;
    width: 48px;
    min-width: 48px;
    height: 26px;
    border-radius: 13px;
    border: none;
    cursor: pointer;
    transition: background 0.2s ease;
    flex-shrink: 0;
  }

  .toggle-on {
    background: #10B981;
  }

  .toggle-off {
    background: var(--color-border-default);
  }

  .toggle-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .toggle-thumb {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  .toggle-thumb-on {
    left: 25px;
  }

  .toggle-thumb-off {
    left: 3px;
  }

  .toggle-label {
    font-size: 13px;
    font-weight: 500;
    margin-left: 8px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .toggle-label-on {
    color: #10B981;
  }

  .toggle-label-off {
    color: var(--color-text-muted);
  }

  /* Empty State */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px;
    color: var(--color-text-muted);
    text-align: center;
  }

  .empty-state p {
    margin-top: 12px;
    font-size: 14px;
  }

  /* Feature Flags Section */
  .feature-flags-section {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 12px;
    padding: 20px;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 16px;
  }

  .feature-flags-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  .feature-flag-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    transition: all 0.15s ease;
  }

  .feature-flag-on {
    border-color: rgba(16, 185, 129, 0.3);
  }

  .feature-flag-name {
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .feature-flag-status {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .change-dot {
    width: 6px;
    height: 6px;
    background: #F59E0B;
    border-radius: 50%;
  }

  .feature-flag-badge {
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
  }

  .badge-on {
    background: rgba(16, 185, 129, 0.15);
    color: #10B981;
  }

  .badge-off {
    background: var(--color-bg-secondary);
    color: var(--color-text-muted);
  }

  /* Responsive */
  @media (max-width: 1024px) {
    .settings-layout {
      flex-direction: column;
    }

    .category-nav {
      width: 100%;
    }

    .category-list {
      flex-direction: row;
      flex-wrap: wrap;
    }

    .category-item {
      flex: 1;
      min-width: 150px;
    }

    .category-desc {
      display: none;
    }

    .feature-flags-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 640px) {
    .page-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .btn {
      width: 100%;
      justify-content: center;
    }

    .category-list {
      flex-direction: column;
    }

    .category-item {
      min-width: unset;
    }

    .setting-input-text {
      width: 100%;
    }

    .feature-flags-grid {
      grid-template-columns: 1fr;
    }

    .quick-stats {
      flex-direction: column;
    }

    /* Fix toggle overlap on mobile */
    .setting-item {
      padding: 16px;
    }

    .setting-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .setting-control {
      width: 100%;
    }

    .setting-input-row {
      justify-content: flex-start;
      width: 100%;
      flex-wrap: wrap;
    }

    .toggle-switch {
      flex-shrink: 0;
    }

    .toggle-label {
      margin-left: 12px;
    }

    .feature-flag-card {
      padding: 12px;
    }

    .feature-flag-name {
      font-size: 11px;
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;
